import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { usePaymentDesignTokens } from '@/constants/residents/mobile/paymentDesignTokens';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { mobilePaymentsStrings } from '@/constants/residents/strings';
import type { PaymentStatus } from '@/features/payments/hooks/useLeaseHistory';

// SVG Icon components
const PendingCheckIcon = RESIDENTS_SVG_COMPONENTS.PENDING_CHECK;
const SuccessCheckIcon = RESIDENTS_SVG_COMPONENTS.SUCCESS_CHECK;
const XFailedIcon = RESIDENTS_SVG_COMPONENTS.X_FAILED;

export type PaymentDesignTokens = ReturnType<typeof usePaymentDesignTokens>;

/**
 * Get the display label for a payment status
 */
export const getStatusLabel = (status: PaymentStatus | undefined): string => {
  switch (status) {
    case 'pending':
      return mobilePaymentsStrings.statusPending;
    case 'successful':
      return mobilePaymentsStrings.statusSuccessful;
    case 'declined':
      return mobilePaymentsStrings.statusDeclined;
    default:
      return '';
  }
};

/**
 * Get the text color for a payment status.
 *
 * Takes the resolved tokens rather than reading them itself: this is a plain
 * function, not a component, so it cannot call a hook. Callers already hold
 * `usePaymentDesignTokens()` for their own styles.
 */
export const getStatusTextColor = (
  status: PaymentStatus | undefined,
  tokens: PaymentDesignTokens,
): string => {
  switch (status) {
    case 'successful':
      return tokens.SUCCESS_GREEN;
    case 'declined':
      return tokens.ERROR_RED;
    case 'pending':
    default:
      return tokens.TEXT_PRIMARY;
  }
};

/**
 * The status icon with its chip background.
 *
 * This was `renderStatusIcon(status)`, a plain function. It is a component now
 * because its chip background has to follow the theme, and only a component can
 * call a hook. The chip was `rgba(238,241,244,0.08)` — a white wash that is
 * invisible on a light surface.
 */
export const PaymentStatusIcon: React.FC<{ status: PaymentStatus | undefined }> = ({ status }) => {
  const { colors } = useThemeColors();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        statusIconContainer: {
          width: 21,
          height: 21,
          borderRadius: 20,
          backgroundColor: colors.activityIcon.bg,
          justifyContent: 'center',
          alignItems: 'center',
        },
        statusIconContainerDeclined: {
          width: 21,
          height: 21,
          borderRadius: 20,
          // Kept as a red wash rather than a theme token: it reads on both
          // surfaces, and the declined icon inside it is a fixed-colour SVG.
          backgroundColor: 'rgba(255, 62, 62, 0.14)',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }),
    [colors],
  );

  const tokens = usePaymentDesignTokens();

  switch (status) {
    case 'pending':
      return PendingCheckIcon ? (
        <View style={styles.statusIconContainer}>
          <PendingCheckIcon width={20} height={20} />
        </View>
      ) : null;
    case 'successful':
      return SuccessCheckIcon ? (
        <View style={styles.statusIconContainer}>
          <SuccessCheckIcon width={20} height={20} color={tokens.SUCCESS_GREEN} />
        </View>
      ) : null;
    case 'declined':
      return XFailedIcon ? (
        <View style={styles.statusIconContainerDeclined}>
          <XFailedIcon width={10} height={10} />
        </View>
      ) : null;
    default:
      return null;
  }
};
